import { array, boolean, object, string } from 'yup';

import { sanitizeAndNormalizeEmail } from '@utils/emailSanitizer';
import { validatePhoneNumber } from '@utils/validation';
import { nameValidation } from '@utils/validationSchemas';

import { getMaxLengthErrMsg, validationErrMessages } from '@constants/validationConstants';
import { MAX_50_LENGTH } from '@constants/validationMessages';
import { EUserFormFields } from './constants';

export const baseSchema = (
	validateEmailFn: (value: string) => Promise<boolean>,
	checkEmailError?: string,
	isJobTitleRequired?: boolean,
) => object({
	[EUserFormFields.firstName]: nameValidation('First Name'),
	[EUserFormFields.lastName]: nameValidation('Last Name'),
	[EUserFormFields.email]: string()
		.default('')
		.transform((value) => sanitizeAndNormalizeEmail(value))
		.lowercase()
		.email(validationErrMessages.validEmail)
		.required(validationErrMessages.email)
		.test(
			'email-unique',
			checkEmailError || validationErrMessages.emailUnique,
			async function (value) {
				if (!value) return true;

				const isFormatValid = await string().email().isValid(value);

				if (!isFormatValid) return true;

				try {
					return await validateEmailFn(value);
				} catch {
					return true;
				}
			},
		),
	[EUserFormFields.phoneNumber]: string()
		.trim()
		.default('')
		.test('validatePhoneNumber', validationErrMessages.contactNumber, validatePhoneNumber),
	[EUserFormFields.is2FA]: boolean(),
	[EUserFormFields.roles]: array()
		.nullable()
		.default(null)
		.required(validationErrMessages.roles),
	[EUserFormFields.jobTitle]: string()
		.trim()
		.default('')
		.max(MAX_50_LENGTH, getMaxLengthErrMsg('Job Title', MAX_50_LENGTH))
		.when([], {
			is: () => isJobTitleRequired,
			then: (schema) => schema.required(validationErrMessages.jobTitle),
		}),
});
